import clsx from 'clsx';
import React, { useEffect } from 'react';
import { useCurrency } from '../../../../../hooks/currency';
import { UiText, UiFlex, UiGrid, UiBox } from '@open-stack/ui';
import { useXSelectable, XSelectablesType } from '../../../../x/selectables/XSelectables';
import { ModifierGroup, ModifierGroupPresentationType, ModifierGroupItem } from '../../../../../XV2';
import { XText, XTextType } from '../../../../x/text/XText';
import { XAlert, XAlertType } from '../../../../x/alert/XAlert';
import { ActiveOrderProduct } from '../../../../../hooks/active-order';
import { XButton, XCard } from '@orderx/lab/ui';
import './OrderProductModifierGroup.scss';

type Props = {
    orderProduct: ActiveOrderProduct;
    modifierGroup: ModifierGroup;
    onSelectModifierGroupItem(modifierGroupItem: ModifierGroupItem): void;
};

export function OrderProductModifierGroup({ orderProduct, modifierGroup, onSelectModifierGroupItem }: Props) {
    const { format } = useCurrency();

    const isNew = !orderProduct.id;
    const { name, description, modifierGroupItems, minAllowed, maxAllowed, autoSelectModifierId, presentationType } =
        modifierGroup;
    const isPackage = presentationType === ModifierGroupPresentationType.PACKAGE;
    const modifierGroupItemsOrdered = modifierGroupItems;
    const isRequired = minAllowed > 0;
    const isMultipleAllowed = maxAllowed > 1;
    const modifierIdsSelected = Object.keys(orderProduct.modifierGroups[modifierGroup.id] || {}).map(Number);
    const isValid = modifierIdsSelected.length >= minAllowed && modifierIdsSelected.length <= maxAllowed;
    const isLimitReached = isMultipleAllowed && modifierIdsSelected.length >= maxAllowed && !isPackage;
    const isMaxAllowedLabel = isMultipleAllowed && maxAllowed < modifierGroupItems.length;

    function selectModifierGroupItem(model: ModifierGroupItem) {
        const canSelect = !model.product?.isOutOfStock && (isPackage || !isLimitReached);

        if (canSelect || isModifierGroupItemSelected(model)) {
            onSelectModifierGroupItem(model);
        }
    }

    function isModifierGroupItemSelected(model: ModifierGroupItem) {
        return Boolean(orderProduct.modifierGroups[model.modifierGroupId]?.[model.id]);
    }

    const { element } = useXSelectable<ModifierGroupItem>({
        type: isMultipleAllowed ? XSelectablesType.MULTIPLE : XSelectablesType.SINGLE,
        onSelect: selectModifierGroupItem,
        models: modifierGroupItemsOrdered,
        modelIsChecked: isModifierGroupItemSelected,
        modelFormatter(model) {
            const name = (model.product || model.modifier).name;
            const isOutOfStock = model.product?.isOutOfStock;
            //const label = [name, getUnitAmountLabel(model)].filter(Boolean).join(', ');

            return {
                key: model.id,
                title: <UiText className={clsx(isOutOfStock && 'out-of-stock')}>{name}</UiText>,
                subtitle: isOutOfStock && 'Out of stock',
                contentRight: <XText>{getPriceLabel(model)}</XText>,
            };
        },
        disabled: isLimitReached,
    });

    useEffect(() => {
        if (isNew) {
            if (isPackage) {
                modifierGroupItemsOrdered.forEach(selectModifierGroupItem);
            } else if (autoSelectModifierId) {
                const result = modifierGroupItems.find((item) => item.id === autoSelectModifierId);

                if (result) {
                    selectModifierGroupItem(result);
                }
            }
        }
    }, [isNew, isPackage, autoSelectModifierId]);

    function getPriceLabel({ price }: ModifierGroupItem) {
        if (price > 0) {
            return `+${format(price)}`;
        } else if (price < 0) {
            return format(price);
        } else {
            return '';
        }
    }

    if (isPackage) {
        return (
            <XCard className={'OrderProductModifierGroup'}>
                <UiGrid gap={0} padding={2}>
                    <XText type={XTextType.TITLE}>{name}</XText>
                    {description && <XText color="text-secondary">{description}</XText>}
                </UiGrid>
            </XCard>
        );
    }

    return (
        <XCard className={clsx('OrderProductModifierGroup', { invalid: !isValid })}>
            <UiGrid gap={0}>
                <UiGrid gap={0} paddingHorizontal={2} paddingTop={2}>
                    <UiFlex justifyContent="space-between">
                        <XText type={XTextType.TITLE} display="block">
                            {name}
                        </XText>

                        {isRequired && (
                            <XText type={XTextType.LABEL} className="required">
                                Required
                            </XText>
                        )}
                    </UiFlex>

                    <UiGrid>
                        {description && <XText color="text-secondary">{description}</XText>}

                        {isMaxAllowedLabel && <XText color="text-secondary">Choose up to {maxAllowed} items</XText>}
                    </UiGrid>
                </UiGrid>

                {isLimitReached && (
                    <UiBox paddingHorizontal={2} paddingTop={2}>
                        <XAlert type={XAlertType.WARNING}>
                            <XText>Maximum item limit reached</XText>
                        </XAlert>
                    </UiBox>
                )}

                {presentationType === ModifierGroupPresentationType.HORIZONTAL && (
                    <UiFlex className={ModifierGroupPresentationType.HORIZONTAL} overflowAuto gap={1} padding={2}>
                        {modifierGroup.modifierGroupItems
                            //.filter((item) => item.product || item.modifier)
                            .map((modifierGroupItem) => (
                                <XButton
                                    onClick={() => selectModifierGroupItem(modifierGroupItem)}
                                    color={isModifierGroupItemSelected(modifierGroupItem) ? 'success' : 'default'}
                                    disabled={isLimitReached && !isModifierGroupItemSelected(modifierGroupItem)}
                                    key={modifierGroupItem.id}>
                                    {(modifierGroupItem.product || modifierGroupItem.modifier).name}
                                </XButton>
                            ))}
                    </UiFlex>
                )}

                {!presentationType && <UiBox padding={2}>{element}</UiBox>}
            </UiGrid>

            {presentationType === ModifierGroupPresentationType.HORIZONTAL && <div className="overflow-layer" />}
        </XCard>
    );
}
